'use client';

import React, { memo } from 'react';
import Reveal from '@/app/components/Reveal';
import { useComponentTranslations } from "@/app/components/LanguageProvider";

const metrics = [
    { id: 'projects', valueKey: 'projectsValue', labelKey: 'projectsLabel', icon: 'projects' },
    { id: 'years', valueKey: 'yearsValue', labelKey: 'yearsLabel', icon: 'years' },
    { id: 'team', valueKey: 'teamValue', labelKey: 'teamLabel', icon: 'team' },
    { id: 'countries', valueKey: 'countriesValue', labelKey: 'countriesLabel', icon: 'countries' },
];

const MetricIcon = memo(({ type, className }: { type: string; className?: string }) => {
    switch (type) {
        case 'projects':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
            );
        case 'years':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'team':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            );
        case 'countries':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        default:
            return null;
    }
});
MetricIcon.displayName = 'MetricIcon';

const MetricsItem = memo(({ metric }: { metric: typeof metrics[0] }) => {
    const { t } = useComponentTranslations("Metrics");

    return (
        <Reveal
            className="group relative flex h-full flex-col items-center justify-center border border-white/10 bg-white/5 px-4 py-8 text-center transition-colors duration-300 hover:border-white/20 hover:bg-white/10 max-md:p-3"
            y={20}
            duration={0.5}
        >
            <div className="mb-4 flex h-12 w-12 items-center justify-center bg-linear-to-br from-cyan-500/20 to-blue-500/20 transition-colors duration-300 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 max-md:mb-2 max-md:h-8 max-md:w-8">
                <MetricIcon type={metric.icon} className="h-6 w-6 text-cyan-400 max-md:h-4 max-md:w-4" />
            </div>
            <div className="text-3xl font-bold text-white max-md:text-xl">
                {t(metric.valueKey)}
            </div>
            <p className="mt-1 text-sm text-white/70 leading-normal max-md:text-[11px] max-md:mt-0.5 max-sm:max-w-[22ch]">
                {t(metric.labelKey)}
            </p>
        </Reveal>
    );
});
MetricsItem.displayName = 'MetricsItem';

const Metrics = () => {
    const { t } = useComponentTranslations("Metrics");

    return (
        <section className="relative py-16 sm:py-24 max-md:py-9">
            <div className="grid grid-cols-2 gap-5 px-4 lg:grid-cols-4 lg:px-8 max-md:gap-3 max-md:px-4">
                {metrics.map((metric) => (
                    <MetricsItem key={metric.id} metric={metric} />
                ))}
            </div>
        </section>
    );
};

export default Metrics;
